import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/config/api";
import { ArrowLeft, X } from "lucide-react";
import { validateName, validateEmail, onlyNameInput } from "@/utils/validation";

export default function NewCustomer({ customer, onClose, onSuccess }) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const isEditing = !!customer;

  const [formData, setFormData] = useState({
    name: customer?.name || "",
    email: customer?.email || "",
    phone: customer?.phone || "",
    address: customer?.address || "",
    company: customer?.company || "",
    opening_balance: "",
  });

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name || "",
        email: customer.email || "",
        phone: customer.phone || "",
        address: customer.address || "",
        company: customer.company || "",
        opening_balance: "",
      });
    }
  }, [customer]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const nameErr = validateName(formData.name, { required: true });
    const emailErr = validateEmail(formData.email, { required: false });
    if (nameErr || emailErr) {
      setError(nameErr || emailErr);
      return;
    }
    setSubmitting(true);

    try {
      if (isEditing) {
        await api.customers.update(customer.id, formData);
      } else {
        await api.customers.create(formData);
      }
      if (onSuccess) {
        onSuccess();
      }
      if (onClose) {
        onClose();
      } else {
        navigate("/customers");
      }
    } catch (error) {
      console.error(
        `Failed to ${isEditing ? "update" : "create"} customer:`,
        error
      );
      setError(
        error.message ||
          `Failed to ${
            isEditing ? "update" : "create"
          } customer. Please try again.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        {!onClose && (
          <button
            onClick={() => navigate("/customers")}
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
            title="Back to Customers"
          >
            <ArrowLeft size={20} className="text-stone-600" />
          </button>
        )}
        <h1 className="text-2xl font-heading font-semibold text-secondary">
          {isEditing ? "Edit Customer" : "New Customer"}
        </h1>
        {onClose && (
          <button
            onClick={onClose}
            className="ml-auto p-2 hover:bg-stone-100 rounded-lg transition-colors"
            title="Close"
          >
            <X size={20} className="text-stone-600" />
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              onKeyDown={onlyNameInput}
              required
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
              placeholder="Customer full name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              Company
            </label>
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
              placeholder="Company name (optional)"
            />
          </div>

          {!isEditing && (
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Opening Balance
              </label>
              <input
                type="number"
                name="opening_balance"
                step="0.01"
                value={formData.opening_balance}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                placeholder="0.00"
              />
              <p className="form-helper mt-1">
                Positive means customer owes you; negative means customer credit.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                placeholder="customer@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                placeholder="+968 XXXX XXXX"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              Address
            </label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              rows="3"
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none resize-none"
              placeholder="Customer address (optional)"
            />
          </div>
        </div>

        <div className="flex gap-4 justify-end">
          <button
            type="button"
            onClick={onClose || (() => navigate("/customers"))}
            className="px-6 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting
              ? isEditing
                ? "Updating..."
                : "Creating..."
              : isEditing
              ? "Update Customer"
              : "Create Customer"}
          </button>
        </div>
      </form>
    </div>
  );
}
